// Theme control — light / dark / system, persisted to localStorage and applied
// as data-theme on <html>. Default is 'system' (follows prefers-color-scheme).

import { useEffect, useState } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
const KEY = 'bizkid:theme';

function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

function apply(pref: ThemePref) {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(read);

  useEffect(() => {
    apply(pref);
    try {
      localStorage.setItem(KEY, pref);
    } catch {
      /* ignore */
    }
  }, [pref]);

  // Resolve what's actually showing (for the toggle icon).
  const resolved: 'light' | 'dark' =
    pref === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : pref;

  return { pref, setPref, resolved };
}

/** Compact pill toggle that cycles light → dark → system. */
export function ThemeToggle() {
  const { pref, setPref, resolved } = useTheme();
  const next: ThemePref = pref === 'light' ? 'dark' : pref === 'dark' ? 'system' : 'light';
  const icon = pref === 'system' ? '🌗' : resolved === 'dark' ? '🌙' : '☀️';
  const label = pref === 'system' ? 'Auto' : resolved === 'dark' ? 'Dark' : 'Light';
  return (
    <button
      type="button"
      onClick={() => setPref(next)}
      aria-label={`Theme: ${label}. Tap to change.`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 38,
        padding: '0 12px',
        borderRadius: 12,
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        color: 'var(--text-2)',
        fontFamily: 'inherit',
        fontWeight: 700,
        fontSize: 13,
        cursor: 'pointer',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}
